import { useEffect, useState } from 'react'
// Map components
import { LayersControl, MapContainer, Marker as RLMarker, TileLayer, useMap, useMapEvents } from 'react-leaflet'

import L from 'leaflet'

// Component css
import 'leaflet/dist/leaflet.css'
import './customMap.css'
// Imports for icons
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'

import { getMessage } from '../../util/IntlMessages'

// Forcers marker icon to render
const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconAnchor: [12, 41]
})
L.Marker.prototype.options.icon = DefaultIcon
// Default view of the map (Brazil, centralized in Brazilia)
const DEFAULT_VIEWPORT = {
  center: [-15.79, -47.88],
  zoom: 4
}

// Base layers available in the layers control (only one active at a time)
const BASE_LAYERS = {
  default: {
    labelKey: 'maps.layer.default',
    url: 'https://{s}.tile.osm.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19
  },
  satelite: {
    labelKey: 'maps.layer.satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    maxZoom: 20
  }
}

// Component
const CustomMap = ({
  latitude,
  longitude,
  zoom,
  setMapLatitude,
  setMapLongitude,
  setMapZoom,
  callbackData,
  disabled,
  mask = 'satelite',
  markersViewOnly
}) => {
  const initialLayer = BASE_LAYERS[mask] ? mask : 'default'
  // When markersViewOnly is provided the map only displays those markers and ignores clicks
  const isReadOnly = Array.isArray(markersViewOnly)

  const compState = {
    mapLatitude: undefined,
    mapLongitude: undefined,
    mapZoom: zoom
  }

  // Props and states
  const [viewport, setViewport] = useState(DEFAULT_VIEWPORT)
  const [marker, setMarker] = useState([])
  const [viewportZoom, setViewportZoom] = useState([zoom])
  const [componentData, setComponentData] = useState(compState)

  // Mount/unmount effect
  useEffect(() => {
    if (latitude && longitude) {
      setViewport({
        center: [latitude, longitude],
        zoom: viewportZoom
      })
      const latlong = []
      latlong.push(latitude)
      latlong.push(longitude)
      setMarker(latlong)
      setComponentData({ ...componentData, mapLongitude: longitude, mapLatitude: latitude })
    } else {
      setViewport(DEFAULT_VIEWPORT)
    }
  }, [latitude, longitude, viewportZoom])

  // Event triggered everytime you click on the map
  const onClickReset = (event) => {
    if (disabled || isReadOnly) return

    // Sets for higher component
    if (setMapLatitude && setMapLongitude) {
      setMapLatitude(event.latlng.lat)
      setMapLongitude(event.latlng.lng)
    }

    const newComponentData = {
      ...componentData,
      mapLongitude: event.latlng.lng,
      mapLatitude: event.latlng.lat
    }
    setComponentData(newComponentData)
    if (callbackData) {
      callbackData(newComponentData)
    }

    // Updates marker on the map
    const latlong = []
    latlong.push(event.latlng.lat)
    latlong.push(event.latlng.lng)
    return setMarker(latlong)
  }

  // Everytime you change the map, it activates this function
  const onViewportChanged = (viewport) => {
    if (setMapZoom) {
      setMapZoom(viewport.zoom)
    }

    const newComponentData = {
      ...componentData,
      mapZoom: viewport.zoom
    }
    setComponentData(newComponentData)
    if (callbackData) {
      callbackData(newComponentData)
    }

    // Updates the map position
    setViewport(viewport)
    setViewportZoom(viewport.zoom)
  }

  // Html component
  return (
    <MapContainer
      viewport={viewport}
      onClick={onClickReset}
      onViewportChanged={onViewportChanged}
      animate={true}
      scrollWheelZoom={false}
      boxZoom={false}
      doubleClickZoom={false}
      center={viewport.center}
      zoom={viewport.zoom}
      style={{ marginBottom: '20px' }}
    >
      <LayersControl position="topright">
        {Object.entries(BASE_LAYERS).map(([layerId, { labelKey, url, attribution, maxZoom }]) => (
          <LayersControl.BaseLayer key={layerId} name={getMessage(labelKey)} checked={layerId === initialLayer}>
            <TileLayer url={url} attribution={attribution} maxZoom={maxZoom} />
          </LayersControl.BaseLayer>
        ))}
      </LayersControl>
      <MapCenterController latitude={latitude} longitude={longitude} />
      {isReadOnly ? (
        markersViewOnly.map((data, index) => <RLMarker key={index} position={[data.lat, data.long]} />)
      ) : (
        <Marker position={marker} setPositionMarker={onClickReset} />
      )}
    </MapContainer>
  )
}

function MapCenterController({ latitude, longitude }) {
  const map = useMap()

  useEffect(() => {
    if (latitude && longitude) {
      map.flyTo([latitude, longitude], map.getZoom())
    }
  }, [latitude, longitude])

  return null
}

function Marker({ position, setPositionMarker }) {
  useMapEvents({
    click: (event) => {
      setPositionMarker(event)
    }
  })

  return position.length && <RLMarker position={position} />
}

export default CustomMap
